package com.elan.edubridge.data.firebase

import com.elan.edubridge.shared.data.firebase.FirebaseNaming
import com.elan.edubridge.shared.domain.model.AcademyApplication
import com.elan.edubridge.shared.domain.model.AcademyApplicationStatus
import com.elan.edubridge.shared.domain.model.AcademyData
import com.elan.edubridge.shared.domain.model.AcademyMembership
import com.elan.edubridge.shared.domain.model.AgeGroup
import com.elan.edubridge.shared.domain.model.Assignment
import com.elan.edubridge.shared.domain.model.AssignmentKind
import com.elan.edubridge.shared.domain.model.AssignmentSubmission
import com.elan.edubridge.shared.domain.model.AttachmentContentType
import com.elan.edubridge.shared.domain.model.AttendanceEvent
import com.elan.edubridge.shared.domain.model.AttendanceEventType
import com.elan.edubridge.shared.domain.model.AttendanceSource
import com.elan.edubridge.shared.domain.model.AttendanceStatus
import com.elan.edubridge.shared.domain.model.AttendanceTimelineEntry
import com.elan.edubridge.shared.domain.model.CalendarEvent
import com.elan.edubridge.shared.domain.model.ClassData
import com.elan.edubridge.shared.domain.model.ClassSchedule
import com.elan.edubridge.shared.domain.model.CommandFailure
import com.elan.edubridge.shared.domain.model.CommandResult
import com.elan.edubridge.shared.domain.model.ContentReport
import com.elan.edubridge.shared.domain.model.ContentReportReason
import com.elan.edubridge.shared.domain.model.ContentReportResolution
import com.elan.edubridge.shared.domain.model.ContentReportStatus
import com.elan.edubridge.shared.domain.model.ContentReportTargetType
import com.elan.edubridge.shared.domain.model.ContentStatus
import com.elan.edubridge.shared.domain.model.DailyRosterEntry
import com.elan.edubridge.shared.domain.model.EventScope
import com.elan.edubridge.shared.domain.model.GuardianConsentStatus
import com.elan.edubridge.shared.domain.model.GuardianContact
import com.elan.edubridge.shared.domain.model.GuardianLink
import com.elan.edubridge.shared.domain.model.JoinApplication
import com.elan.edubridge.shared.domain.model.JoinApplicationStatus
import com.elan.edubridge.shared.domain.model.LinkStatus
import com.elan.edubridge.shared.domain.model.MembershipStatus
import com.elan.edubridge.shared.domain.model.NoticeData
import com.elan.edubridge.shared.domain.model.Permission
import com.elan.edubridge.shared.domain.model.PostAttachment
import com.elan.edubridge.shared.domain.model.PostComment
import com.elan.edubridge.shared.domain.model.PostData
import com.elan.edubridge.shared.domain.model.ProblemBankSpec
import com.elan.edubridge.shared.domain.model.PushDeliverySummary
import com.elan.edubridge.shared.domain.model.StudentData
import com.elan.edubridge.shared.domain.model.SubmissionSource
import com.elan.edubridge.shared.domain.model.UserAccount
import com.elan.edubridge.shared.domain.model.UserRole

/**
 * Firestore `dynamic` 문서 ↔ domain 모델 변환 계층.
 *
 * ⚠️ **Timestamp 규칙(M0-5 실측 결론)**: `JSON.stringify(snapshot.data())` ->
 * kotlinx-serialization 왕복은 Timestamp 필드에서 깨진다(Timestamp가 `{seconds, nanoseconds,
 * type}` 객체로 직렬화되어 String 필드로 선언한 DTO가 디코드에 실패한다). 이 파일은 그 왕복을
 * 전혀 쓰지 않는다 — `snapshot.data()`의 dynamic 필드에 직접 접근해
 * `.toDate().toISOString()`으로 String으로 명시 변환한다([timestampToIsoOrNull]).
 *
 * **필드명 규약**: Firestore 문서 필드명은 domain 모델 프로퍼티명과 1:1로 맞춘다 —
 * `functions/src/common/membership.ts`의 "Field names mirror `AcademyMembership` in `shared`"
 * 주석과 동일한 스택 전역 관례다.
 *
 * **함수 모양(dynamic을 항상 파라미터로 받고 확장 함수로 만들지 않는 이유, 실측)**: Kotlin/JS는
 * 최상위(top-level) dynamic 리시버 확장 함수/프로퍼티를 **가시성과 무관하게** 허용하지 않는다
 * ("Dynamic receivers are prohibited" — `private`로 선언해도 동일하게 컴파일 에러가 난다). 그래서
 * 이 파일의 모든 헬퍼·매퍼는 `fun xxx(data: dynamic, ...)` 형태의 평범한 함수로 쓰고
 * `data.xxx(...)`가 아니라 `xxx(data, ...)`로 호출한다.
 */

/**
 * `edubridge_academies/{academyId}` 하위 서브컬렉션 이름. `functions/src/common/collections.ts`의
 * `SUBCOLLECTIONS`와 반드시 동일해야 한다(하위 컬렉션은 `edubridge_` 접두사 없이 원래 이름을 쓴다).
 */
internal object AcademySubcollections {
    const val MEMBERS = "members"
    const val STUDENTS = "students"
    const val GUARDIAN_LINKS = "guardianLinks"
    const val CLASSES = "classes"
    const val ENROLLMENTS = "enrollments"
    const val ATTENDANCE_RECORDS = "attendanceRecords"
    const val ATTENDANCE_EVENTS = "attendanceEvents"
    const val QR_SESSIONS = "qrSessions"
    const val NOTICES = "notices"
    const val POSTS = "posts"
    const val CONTENT_REPORTS = "contentReports"

    /** W7 가입 신청 정본. 문서 id가 신청자 uid라 스태프에게만 read가 열린다(rules). */
    const val JOIN_APPLICATIONS = "joinApplications"
    const val ASSIGNMENTS = "assignments"

    /** 과제 하위. 한 과제의 학생별 제출 결과이며 문서 id가 studentId다. */
    const val SUBMISSIONS = "submissions"

    /**
     * 학원 캘린더. [UserSubcollections.EVENTS]와 이름이 같은 것은 의도된 것이다 —
     * 두 캘린더를 가르는 것이 경로이기 때문이다(`functions/src/common/collections.ts`).
     * 이름이 겹치므로 **collectionGroup으로 조회하면 안 된다**: rules가 그 경로를 열어 두지
     * 않았고, 열더라도 남의 개인 일정이 같은 결과에 섞인다.
     */
    const val EVENTS = "events"
}

/** `posts/{postId}` 하위. 댓글은 게시글에만 달린다 — 공지에는 이 경로가 없다. */
internal object PostSubcollections {
    const val COMMENTS = "comments"
}

/** `edubridge_users/{uid}` 하위 서브컬렉션 이름. `functions/src/common/collections.ts`와 동일해야 한다. */
internal object UserSubcollections {
    const val MEMBERSHIPS = "memberships"
    const val INSTALLATIONS = "installations"
    const val NOTIFICATIONS = "notifications"
    const val ATTENDANCE_TIMELINE = "attendanceTimeline"

    /** 개인 캘린더. 격리는 경로가 보장한다 — [ATTENDANCE_TIMELINE]과 같은 구조다. */
    const val EVENTS = "events"

    /** W7 가입 신청 본인 미러. 문서 id는 학원 id고 본인만 읽는다(rules). */
    const val JOIN_APPLICATIONS = "joinApplications"
}

/** 최상위 컬렉션. `FirebaseNaming`에 상수가 없는 것만 여기 보충한다(`functions/src/common/collections.ts` 대조). */
internal object TopLevelCollections {
    const val ACADEMY_APPLICATIONS = "${FirebaseNaming.FIRESTORE_PREFIX}academyApplications"
}

// jsTypeOf/stringOrNull/intOrDefault/boolOrDefault는 internal이다 — dynamic을 리시버가 아니라
// 파라미터로 받으므로 "Dynamic receivers are prohibited" 제약이 적용되지 않아 internal로 넘겨도
// 안전하다(실측).
// FirebaseAcademyCommandDataSource.kt·FirebaseEduConnectDataSource.kt·WebGraph.kt가 재사용한다.
// 나머지(stringOrDefault 등)는 Dto.kt의 매퍼들만 쓰므로 private로 남긴다.

internal fun jsTypeOf(value: dynamic): String = js("typeof value") as String

internal fun stringOrNull(source: dynamic, field: String): String? {
    val value = source[field]
    return if (jsTypeOf(value) == "string") value as String else null
}

private fun stringOrDefault(source: dynamic, field: String, default: String): String =
    stringOrNull(source, field) ?: default

internal fun intOrDefault(source: dynamic, field: String, default: Int): Int {
    val value = source[field]
    return if (jsTypeOf(value) == "number") (value as Double).toInt() else default
}

internal fun boolOrDefault(source: dynamic, field: String, default: Boolean): Boolean {
    val value = source[field]
    return if (jsTypeOf(value) == "boolean") value as Boolean else default
}

/**
 * 시각 필드를 ISO-8601 String으로 변환한다. undefined/null이면 null(M0-5 결론).
 *
 * **두 표현을 모두 받는다**: `functions/src` 아래 대부분의 TS 파일은 실측상 거의 모든 시각 필드를
 * `new Date().toISOString()` **String**으로 직접 저장한다(예: `attendance.ts`의
 * `at = new Date().toISOString()`, `roles.ts`/`membership.ts` 동일). 반면 클라이언트가 직접 쓰는
 * `edubridge_users/{uid}.createdAt`(register())처럼 `serverTimestamp()`로 쓴 필드는 읽을 때
 * 진짜 Firestore `Timestamp` 인스턴스로 돌아온다. 이미 String이면 그대로 반환하고, 아니면
 * Timestamp로 가정해 `.toDate().toISOString()`으로 변환한다 — 둘 중 어느 쪽이 오든 안전하다.
 */
private fun timestampToIsoOrNull(source: dynamic, field: String): String? {
    val value = source[field]
    if (value == null || jsTypeOf(value) == "undefined") return null
    if (jsTypeOf(value) == "string") return value as String
    val date = value.toDate()
    if (date == null || jsTypeOf(date) == "undefined") return null
    return date.toISOString() as? String
}

private fun stringListOrEmpty(source: dynamic, field: String): List<String> {
    val value = source[field]
    if (js("!Array.isArray(value)") as Boolean) return emptyList()
    val array = value.unsafeCast<Array<dynamic>>()
    val result = mutableListOf<String>()
    for (item in array) {
        if (jsTypeOf(item) == "string") result.add(item as String)
    }
    return result
}

private fun intListOrEmpty(source: dynamic, field: String): List<Int> {
    val value = source[field]
    if (js("!Array.isArray(value)") as Boolean) return emptyList()
    val array = value.unsafeCast<Array<dynamic>>()
    val result = mutableListOf<Int>()
    for (item in array) {
        if (jsTypeOf(item) == "number") result.add((item as Double).toInt())
    }
    return result
}

private fun guardianContactsOrEmpty(source: dynamic, field: String): List<GuardianContact> {
    val value = source[field]
    if (js("!Array.isArray(value)") as Boolean) return emptyList()
    val array = value.unsafeCast<Array<dynamic>>()
    val result = mutableListOf<GuardianContact>()
    for (item in array) {
        result.add(
            GuardianContact(
                name = stringOrDefault(item, "name", ""),
                phone = stringOrDefault(item, "phone", ""),
                relation = stringOrDefault(item, "relation", ""),
            ),
        )
    }
    return result
}

private fun toClassSchedule(source: dynamic, field: String): ClassSchedule {
    val value = source[field]
    if (value == null || jsTypeOf(value) == "undefined") return ClassSchedule()
    return ClassSchedule(
        daysOfWeek = intListOrEmpty(value, "daysOfWeek"),
        startMinuteOfDay = intOrDefault(value, "startMinuteOfDay", 0),
        endMinuteOfDay = intOrDefault(value, "endMinuteOfDay", 0),
    )
}

private inline fun <reified T : Enum<T>> enumOrDefault(source: dynamic, field: String, default: T): T {
    val raw = stringOrNull(source, field) ?: return default
    return enumValues<T>().firstOrNull { it.name == raw } ?: default
}

// ---- 계정 (§S4 3) ----

/** `edubridge_users/{uid}` 문서 + Auth가 제공하는 uid/email/globalRoles를 합쳐 [UserAccount]를 만든다. */
internal fun toUserAccount(data: dynamic, uid: String, email: String?, globalRoles: Set<UserRole>): UserAccount =
    UserAccount(
        id = uid,
        email = email ?: stringOrDefault(data, "email", ""),
        name = stringOrDefault(data, "name", ""),
        baseRole = enumOrDefault(data, "baseRole", UserRole.STUDENT),
        globalRoles = globalRoles,
        birthDate = stringOrNull(data, "birthDate"),
        ageGroup = enumOrDefault(data, "ageGroup", AgeGroup.UNKNOWN),
        consentStatus = enumOrDefault(data, "consentStatus", GuardianConsentStatus.NOT_REQUIRED),
    )

/** `edubridge_users/{uid}/memberships/{academyId}` 미러 문서 → [AcademyMembership]. */
internal fun toAcademyMembership(data: dynamic): AcademyMembership =
    AcademyMembership(
        academyId = stringOrDefault(data, "academyId", ""),
        academyName = stringOrDefault(data, "academyName", ""),
        userId = stringOrDefault(data, "userId", ""),
        role = enumOrDefault(data, "role", UserRole.STUDENT),
        permission = enumOrDefault(data, "permission", Permission.VIEWER),
        status = enumOrDefault(data, "status", MembershipStatus.REVOKED),
        assignedClassIds = stringListOrEmpty(data, "assignedClassIds"),
        invitedBy = stringOrNull(data, "invitedBy"),
        activatedAt = timestampToIsoOrNull(data, "activatedAt"),
    )

// ---- 카탈로그 (§S4 4) ----

internal fun toAcademyData(data: dynamic, id: String): AcademyData =
    AcademyData(
        id = id,
        name = stringOrDefault(data, "name", ""),
        description = stringOrDefault(data, "description", ""),
        phone = stringOrDefault(data, "contactPhone", stringOrDefault(data, "phone", "")),
        address = stringOrDefault(data, "address", ""),
        studentCount = intOrDefault(data, "studentCount", 0),
        teacherCount = intOrDefault(data, "teacherCount", 0),
        timeZone = stringOrDefault(data, "timeZone", "Asia/Seoul"),
        lateThresholdMinutes = intOrDefault(data, "lateThresholdMinutes", 10),
        qrRotationSeconds = intOrDefault(data, "qrRotationSeconds", 20),
        qrGraceSeconds = intOrDefault(data, "qrGraceSeconds", 15),
        minScanIntervalSeconds = intOrDefault(data, "minScanIntervalSeconds", 60),
    )

/**
 * `edubridge_academies/{academyId}/classes/{id}` → [ClassData].
 *
 * **함정**: `functions/src/provisioning.ts`의 `createClass`는 `teacherUserId`만 저장하고
 * 담당 선생님의 표시 이름은 비정규화해 두지 않는다. [ClassData.teacher]는 여기서는 빈 문자열로
 * 남는다 — 이름 표시가 필요하면 별도로 `members/{teacherUserId}` 조회가 필요하다(S1/S2 스키마 간극,
 * 이 lane의 범위 밖).
 */
internal fun toClassData(data: dynamic, id: String, academyId: String): ClassData =
    ClassData(
        id = id,
        academyId = academyId,
        className = stringOrDefault(data, "name", stringOrDefault(data, "className", "")),
        subject = stringOrDefault(data, "subject", ""),
        teacherUserId = stringOrNull(data, "teacherUserId"),
        teacher = stringOrDefault(data, "teacher", ""),
        schedule = toClassSchedule(data, "schedule"),
        studentCount = intOrDefault(data, "studentCount", 0),
    )

internal fun toStudentData(data: dynamic, id: String, academyId: String): StudentData =
    StudentData(
        id = id,
        academyId = academyId,
        userId = stringOrNull(data, "userId"),
        name = stringOrDefault(data, "name", ""),
        grade = stringOrDefault(data, "grade", ""),
        phone = stringOrNull(data, "phone"),
        guardianContacts = guardianContactsOrEmpty(data, "guardianContacts"),
    )

internal fun toNoticeData(data: dynamic, id: String, academyId: String): NoticeData =
    NoticeData(
        id = id,
        academyId = academyId,
        title = stringOrDefault(data, "title", ""),
        date = timestampToIsoOrNull(data, "date") ?: "",
        content = stringOrDefault(data, "content", ""),
        contentHtml = stringOrNull(data, "contentHtml"),
        status = enumOrDefault(data, "status", ContentStatus.ACTIVE),
        authorUid = stringOrNull(data, "authorUid"),
        authorName = stringOrNull(data, "authorName"),
    )

// ---- 게시판 ----

internal fun toPostData(data: dynamic, id: String, academyId: String): PostData =
    PostData(
        id = id,
        academyId = academyId,
        title = stringOrDefault(data, "title", ""),
        content = stringOrDefault(data, "content", ""),
        status = enumOrDefault(data, "status", ContentStatus.ACTIVE),
        authorUid = stringOrNull(data, "authorUid"),
        authorName = stringOrNull(data, "authorName"),
        commentCount = intOrDefault(data, "commentCount", 0),
        attachments = postAttachmentsOrEmpty(data, "attachments"),
        createdAt = timestampToIsoOrNull(data, "createdAt") ?: "",
        updatedAt = timestampToIsoOrNull(data, "updatedAt"),
    )

internal fun toPostComment(data: dynamic, id: String, academyId: String, postId: String): PostComment =
    PostComment(
        id = id,
        postId = postId,
        academyId = academyId,
        content = stringOrDefault(data, "content", ""),
        status = enumOrDefault(data, "status", ContentStatus.ACTIVE),
        authorUid = stringOrNull(data, "authorUid"),
        authorName = stringOrNull(data, "authorName"),
        createdAt = timestampToIsoOrNull(data, "createdAt") ?: "",
    )

/** 신고 대기열은 스태프 전용 경로에서만 읽는다. UI가 uid 필드를 렌더하지 않도록 모델로만 넘긴다. */
internal fun toContentReport(data: dynamic, id: String, academyId: String): ContentReport =
    ContentReport(
        id = id,
        academyId = academyId,
        targetType = enumOrDefault(data, "targetType", ContentReportTargetType.POST),
        targetId = stringOrDefault(data, "targetId", ""),
        postId = stringOrNull(data, "postId"),
        targetAuthorUid = stringOrNull(data, "targetAuthorUid"),
        reason = enumOrDefault(data, "reason", ContentReportReason.OTHER),
        detail = stringOrNull(data, "detail"),
        reporterUid = stringOrDefault(data, "reporterUid", ""),
        status = enumOrDefault(data, "status", ContentReportStatus.OPEN),
        resolution = stringOrNull(data, "resolution")?.let { raw ->
            ContentReportResolution.entries.firstOrNull { it.name == raw }
        },
        resolutionNote = stringOrNull(data, "resolutionNote"),
        resolvedAt = timestampToIsoOrNull(data, "resolvedAt"),
        resolvedBy = stringOrNull(data, "resolvedBy"),
        reportCount = intOrDefault(data, "reportCount", 1),
        createdAt = timestampToIsoOrNull(data, "createdAt") ?: "",
    )

/**
 * W7 가입 신청 한 행. 정본(문서 id = 신청자 uid)과 본인 미러(문서 id = 학원 id)가 같은
 * payload를 쓰므로 매퍼도 하나다 — 두 id는 필드로도 저장돼 있어 필드를 정본으로 읽고,
 * 비어 있을 때만 호출부가 경로에서 아는 값으로 메꾼다.
 *
 * `join.ts`는 시각을 Firestore Timestamp가 아니라 ISO-8601 **문자열로 저장**하므로
 * (`new Date().toISOString()` 그대로 set) `timestampToIsoOrNull`이 아니라 `stringOrNull`로 읽는다.
 */
internal fun toJoinApplication(
    data: dynamic,
    fallbackAcademyId: String,
    fallbackApplicantUid: String,
): JoinApplication = JoinApplication(
    academyId = stringOrDefault(data, "academyId", fallbackAcademyId),
    academyName = stringOrDefault(data, "academyName", ""),
    applicantUid = stringOrDefault(data, "applicantUid", fallbackApplicantUid),
    applicantName = stringOrNull(data, "applicantName"),
    applicantEmail = stringOrNull(data, "applicantEmail"),
    status = enumOrDefault(data, "status", JoinApplicationStatus.PENDING),
    attemptCount = intOrDefault(data, "attemptCount", 1),
    consentAgreedAt = stringOrNull(data, "consentAgreedAt"),
    submittedAt = stringOrDefault(data, "submittedAt", ""),
    firstSubmittedAt = stringOrNull(data, "firstSubmittedAt"),
    decidedBy = stringOrNull(data, "decidedBy"),
    decidedAt = stringOrNull(data, "decidedAt"),
    studentId = stringOrNull(data, "studentId"),
    updatedAt = stringOrNull(data, "updatedAt"),
)

/** 게시글 문서에 인라인된 첨부 배열 — 목록 화면이 추가 호출 없이 첨부 유무를 그릴 수 있게 한 것이다. */
private fun postAttachmentsOrEmpty(source: dynamic, field: String): List<PostAttachment> {
    val value = source[field]
    if (js("!Array.isArray(value)") as Boolean) return emptyList()
    val array = value.unsafeCast<Array<dynamic>>()
    return array.mapNotNull { toPostAttachmentOrNull(it) }
}

/**
 * `id`·`objectPath`가 없는 항목은 열 방법이 없으므로 버린다.
 *
 * `contentType`은 allowlist에 없으면 null이 된다([AttachmentContentType.fromMimeType]) —
 * 서버가 넷 중 하나만 저장하므로 실제로는 서버 allowlist가 먼저 늘어난 경우뿐이다.
 */
internal fun toPostAttachmentOrNull(item: dynamic): PostAttachment? {
    if (item == null || jsTypeOf(item) != "object") return null
    val id = stringOrNull(item, "id") ?: return null
    val objectPath = stringOrNull(item, "objectPath") ?: return null
    return PostAttachment(
        id = id,
        fileName = stringOrDefault(item, "fileName", ""),
        contentType = AttachmentContentType.fromMimeType(stringOrNull(item, "contentType")),
        sizeBytes = intOrDefault(item, "sizeBytes", 0),
        objectPath = objectPath,
        createdAt = stringOrNull(item, "createdAt"),
        createdBy = stringOrNull(item, "createdBy"),
    )
}

// ---- 일정 ----

/**
 * `events/{id}` → [CalendarEvent]. 학원 캘린더와 개인 캘린더가 같은 문서 모양이라 한 함수로
 * 쓰고, 문서에 없는 [CalendarEvent.scope]는 어느 컬렉션에서 읽었는지 아는 호출부가 넘긴다.
 *
 * [academyId]는 학원 일정에서만 채운다 — 개인 일정 문서에는 그 필드 자체가 없다.
 */
internal fun toCalendarEvent(
    data: dynamic,
    id: String,
    scope: EventScope,
    academyId: String?,
): CalendarEvent =
    CalendarEvent(
        id = id,
        scope = scope,
        academyId = academyId,
        title = stringOrDefault(data, "title", ""),
        description = stringOrDefault(data, "description", ""),
        startAt = timestampToIsoOrNull(data, "startAt") ?: "",
        endAt = timestampToIsoOrNull(data, "endAt") ?: "",
        allDay = boolOrDefault(data, "allDay", false),
        status = enumOrDefault(data, "status", ContentStatus.ACTIVE),
        createdBy = stringOrNull(data, "createdBy"),
        createdByName = stringOrNull(data, "createdByName"),
        createdAt = timestampToIsoOrNull(data, "createdAt") ?: "",
        updatedAt = timestampToIsoOrNull(data, "updatedAt"),
    )

/** `problemBank`가 없으면 null이다 — [AssignmentKind.NOTICE] 과제에는 원래 없는 블록이다. */
private fun toProblemBankSpec(data: dynamic, field: String): ProblemBankSpec? {
    val value = data[field]
    if (value == null || jsTypeOf(value) == "undefined") return null
    return ProblemBankSpec(
        subject = stringOrDefault(value, "subject", ""),
        grade = stringOrDefault(value, "grade", ""),
        difficulty = stringOrDefault(value, "difficulty", ""),
        questionType = stringOrDefault(value, "questionType", ""),
        questionCount = intOrDefault(value, "questionCount", 0),
        combinationKey = stringOrDefault(value, "combinationKey", ""),
    )
}

internal fun toAssignment(data: dynamic, id: String, academyId: String): Assignment =
    Assignment(
        id = id,
        academyId = academyId,
        classId = stringOrNull(data, "classId"),
        title = stringOrDefault(data, "title", ""),
        teacher = stringOrDefault(data, "teacher", ""),
        dueDate = timestampToIsoOrNull(data, "dueDate") ?: "",
        completed = boolOrDefault(data, "completed", false),
        // `kind`가 없는 문서는 이 필드가 생기기 전 과제다 — 안내 과제로 읽는다.
        kind = enumOrDefault(data, "kind", AssignmentKind.NOTICE),
        problemBank = toProblemBankSpec(data, "problemBank"),
        attachments = postAttachmentsOrEmpty(data, "attachments"),
        status = enumOrDefault(data, "status", ContentStatus.ACTIVE),
    )

internal fun toAssignmentSubmission(
    data: dynamic,
    studentId: String,
    academyId: String,
    assignmentId: String,
): AssignmentSubmission {
    val submittedAt = timestampToIsoOrNull(data, "submittedAt") ?: ""
    return AssignmentSubmission(
        assignmentId = assignmentId,
        academyId = academyId,
        studentId = studentId,
        studentUserId = stringOrDefault(data, "studentUserId", ""),
        studentName = stringOrDefault(data, "studentName", ""),
        correct = intOrDefault(data, "correct", 0),
        total = intOrDefault(data, "total", 0),
        scorePercent = intOrDefault(data, "scorePercent", 0),
        elapsedSeconds = intOrDefault(data, "elapsedSeconds", 0),
        submittedAt = submittedAt,
        firstSubmittedAt = timestampToIsoOrNull(data, "firstSubmittedAt") ?: submittedAt,
        attempts = intOrDefault(data, "attempts", 1),
        source = enumOrDefault(data, "source", SubmissionSource.EDUMINT),
    )
}

// ---- 출결 (§S4 4) ----

internal fun toAttendanceEvent(data: dynamic, id: String): AttendanceEvent =
    AttendanceEvent(
        eventId = id,
        academyId = stringOrDefault(data, "academyId", ""),
        studentId = stringOrDefault(data, "studentId", ""),
        studentName = stringOrDefault(data, "studentName", ""),
        classId = stringOrNull(data, "classId"),
        className = stringOrNull(data, "className"),
        type = enumOrDefault(data, "type", AttendanceEventType.CHECK_IN),
        status = enumOrDefault(data, "status", AttendanceStatus.NOT_ARRIVED),
        occurredAt = timestampToIsoOrNull(data, "occurredAt") ?: "",
        serviceDate = stringOrDefault(data, "serviceDate", ""),
        source = enumOrDefault(data, "source", AttendanceSource.MANUAL),
        actorUid = stringOrNull(data, "actorUid"),
        sessionId = stringOrNull(data, "sessionId"),
        nonceHash = stringOrNull(data, "nonceHash"),
        previousEventId = stringOrNull(data, "previousEventId"),
    )

internal fun toDailyRosterEntry(data: dynamic, academyId: String): DailyRosterEntry =
    DailyRosterEntry(
        academyId = academyId,
        serviceDate = stringOrDefault(data, "serviceDate", ""),
        classId = stringOrDefault(data, "classId", ""),
        className = stringOrDefault(data, "className", ""),
        studentId = stringOrDefault(data, "studentId", ""),
        studentName = stringOrDefault(data, "studentName", ""),
        status = enumOrDefault(data, "status", AttendanceStatus.NOT_ARRIVED),
        lateArrival = boolOrDefault(data, "lateArrival", false),
        lastEventId = stringOrNull(data, "lastEventId"),
        lastEventAt = timestampToIsoOrNull(data, "lastEventAt"),
        checkInAt = timestampToIsoOrNull(data, "checkInAt"),
        checkOutAt = timestampToIsoOrNull(data, "checkOutAt"),
    )

internal fun toAttendanceTimelineEntry(data: dynamic, id: String): AttendanceTimelineEntry =
    AttendanceTimelineEntry(
        eventId = stringOrDefault(data, "eventId", id),
        subjectStudentId = stringOrDefault(data, "subjectStudentId", ""),
        subjectStudentName = stringOrDefault(data, "subjectStudentName", ""),
        academyId = stringOrDefault(data, "academyId", ""),
        academyName = stringOrDefault(data, "academyName", ""),
        classId = stringOrNull(data, "classId"),
        className = stringOrNull(data, "className"),
        status = enumOrDefault(data, "status", AttendanceStatus.NOT_ARRIVED),
        occurredAt = timestampToIsoOrNull(data, "occurredAt") ?: "",
        serviceDate = stringOrDefault(data, "serviceDate", ""),
        source = enumOrDefault(data, "source", AttendanceSource.MANUAL),
    )

internal fun toGuardianLink(data: dynamic): GuardianLink =
    GuardianLink(
        academyId = stringOrDefault(data, "academyId", ""),
        guardianUserId = stringOrDefault(data, "guardianUserId", ""),
        studentId = stringOrDefault(data, "studentId", ""),
        studentUserId = stringOrNull(data, "studentUserId"),
        studentName = stringOrDefault(data, "studentName", ""),
        relation = stringOrDefault(data, "relation", ""),
        status = enumOrDefault(data, "status", LinkStatus.REVOKED),
        linkedAt = timestampToIsoOrNull(data, "linkedAt"),
    )

/**
 * `edubridge_academyApplications/{id}` → [AcademyApplication]. 필드명은 `functions/src/roles.ts`의
 * `submitAcademyApplication`/`approveAcademyApplication` 쓰기와 1:1로 맞춘다.
 */
internal fun toAcademyApplication(data: dynamic, id: String): AcademyApplication =
    AcademyApplication(
        id = id,
        academyName = stringOrDefault(data, "academyName", ""),
        applicantUid = stringOrDefault(data, "applicantUid", ""),
        contactPhone = stringOrNull(data, "contactPhone"),
        status = enumOrDefault(data, "status", AcademyApplicationStatus.PENDING),
        createdAt = timestampToIsoOrNull(data, "createdAt") ?: "",
        academyId = stringOrNull(data, "academyId"),
        decidedBy = stringOrNull(data, "decidedBy"),
        decidedAt = timestampToIsoOrNull(data, "decidedAt"),
    )

// ---- 콜러블 실패 매핑 (§S4 5) ----

/**
 * Firebase Functions `HttpsError.code`를 [CommandFailure]로 변환한다.
 *
 * 코드는 `"functions/permission-denied"`처럼 `"functions/"` 접두사가 붙어 온다(firebase@12.17.0
 * `@firebase/functions` 타입 선언 실측: `FunctionsErrorCode = \`functions/\${FunctionsErrorCodeCore}\``).
 * 접두사 유무에 관계없이 동작하도록 마지막 세그먼트만 비교한다.
 */
internal fun httpsErrorCodeToCommandFailure(code: String?): CommandFailure {
    val normalized = code?.substringAfterLast('/')
    return when (normalized) {
        "unauthenticated" -> CommandFailure.UNAUTHENTICATED
        "permission-denied" -> CommandFailure.PERMISSION_DENIED
        "not-found" -> CommandFailure.NOT_FOUND
        "invalid-argument" -> CommandFailure.INVALID_ARGUMENT
        "failed-precondition" -> CommandFailure.FAILED_PRECONDITION
        "already-exists" -> CommandFailure.ALREADY_EXISTS
        "resource-exhausted" -> CommandFailure.RESOURCE_EXHAUSTED
        "unavailable" -> CommandFailure.UNAVAILABLE
        else -> CommandFailure.INTERNAL
    }
}

/**
 * 캐치한 `Throwable`(원래 JS `FirebaseError`)에서 `.code`/`.message`를 동적으로 읽는다.
 * Auth(`auth/` 접두사)·Functions(`functions/` 접두사) 에러 모두 같은 `FirebaseError` 기반 구조라 공용이다.
 */
internal fun Throwable.jsErrorCode(): String? = asDynamic().code as? String

internal fun Throwable.jsErrorMessageOrDefault(default: String): String =
    (asDynamic().message as? String)?.takeIf { it.isNotBlank() } ?: default

/** 콜러블 실패 시 공용 폴백 문구. 구체적인 문구를 못 얻었을 때만 쓴다. */
internal const val GENERIC_COMMAND_ERROR_MESSAGE = "요청을 처리하지 못했어요. 잠시 후 다시 시도해 주세요."

/** `requiredHeaders`처럼 문자열만 담긴 중첩 객체. String이 아닌 항목은 실을 수 없으므로 버린다. */
internal fun stringMapOrEmpty(source: dynamic, field: String): Map<String, String> {
    val value = source[field]
    if (value == null || jsTypeOf(value) != "object") return emptyMap()
    val keys = js("Object.keys(value)").unsafeCast<Array<String>>()
    val result = mutableMapOf<String, String>()
    for (key in keys) {
        val entry = value[key]
        if (jsTypeOf(entry) == "string") result[key] = entry as String
    }
    return result
}

/** 공지·댓글 콜러블의 `notified` 블록. 없으면 팬아웃 자체가 보고되지 않은 것이라 null이다. */
internal fun pushDeliverySummaryOrNull(source: dynamic, field: String): PushDeliverySummary? {
    val notified = source[field]
    if (notified == null || jsTypeOf(notified) != "object") return null
    return PushDeliverySummary(
        recipients = intOrDefault(notified, "recipients", 0),
        installations = intOrDefault(notified, "installations", 0),
        delivered = intOrDefault(notified, "delivered", 0),
        truncated = boolOrDefault(notified, "truncated", false),
    )
}

/**
 * `edubridge-recordAttendanceBatch` 응답의 `results` 배열 → [CommandResult] 목록.
 *
 * 서버가 개별 항목 실패를 캐치해 `{studentId, ok: false, code, message}`로 **데이터**(예외 아님)로
 * 돌려준다(`functions/src/attendance.ts`의 `recordAttendanceBatch`, 부분 성공 의미론). 여기 `code`는
 * 서버가 `HttpsError`를 던지지 않고 `error.code`를 직접 담은 값이라 `"functions/"` 접두사가 없다 —
 * [httpsErrorCodeToCommandFailure]는 접두사 유무 모두 처리하므로 그대로 재사용한다.
 */
internal fun toBatchEntryResults(data: dynamic): List<CommandResult> {
    val raw = data["results"]
    if (js("!Array.isArray(raw)") as Boolean) return emptyList()
    val array = raw.unsafeCast<Array<dynamic>>()
    return array.map { item ->
        if (boolOrDefault(item, "ok", false)) {
            CommandResult(entityId = stringOrNull(item, "eventId"))
        } else {
            CommandResult(
                failure = httpsErrorCodeToCommandFailure(stringOrNull(item, "code")),
                errorMessage = stringOrNull(item, "message") ?: GENERIC_COMMAND_ERROR_MESSAGE,
            )
        }
    }
}
